import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { InfoIcon } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';

interface MetricCardProps {
  /** Card heading. Caller wraps the text in <T> so it stays translatable. */
  label: ReactNode;
  /** Optional help text shown in a tooltip beside the heading. */
  tooltip?: ReactNode;
  /** Headline value, pre-formatted by the caller (currency, percentage, etc.). */
  value: ReactNode;
  /** Tailwind classes applied to the value text, e.g. 'text-red-600'. */
  valueClassName?: string;
  /** Optional supporting line rendered under the value. */
  subtitle?: ReactNode;
  isLoading?: boolean;
  /** Classes for the card itself, e.g. to fill a dashboard cell. */
  className?: string;
}

/**
 * An info icon beside a metric's heading, with help text on hover, focus or
 * a tap: touch screens don't hover, so a press opens it too (and doesn't
 * close it, as a tooltip trigger does by default).
 */
function MetricInfo({ children }: { children: ReactNode }) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild closeOnClick={false}>
          {/* A button so keyboard users can focus it and open the help. */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={gt('More info', {
              $context:
                'accessible name of the info icon beside a metric title',
            })}
            className="inline-flex cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <InfoIcon
              aria-hidden
              className="h-3.5 w-3.5 text-muted-foreground/60"
            />
          </button>
        </TooltipTrigger>
        {/* Fill each line (the base balances them, leaving a ragged box). */}
        <TooltipContent side="bottom" className="max-w-72 text-pretty">
          <p>{children}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Presentational KPI card used across AR dashboards. It is agnostic of where
 * its numbers come from — the org-wide summary and the single-customer
 * collections profile both feed it their own pre-formatted values.
 */
export function MetricCard({
  label,
  tooltip,
  value,
  valueClassName,
  subtitle,
  isLoading = false,
  className,
}: MetricCardProps) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
          {label}
          {tooltip != null && <MetricInfo>{tooltip}</MetricInfo>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <p className={`text-2xl font-bold ${valueClassName ?? ''}`}>
            {value}
          </p>
        )}
        {!isLoading && subtitle != null && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}
